import React, { useEffect, useState, useRef } from "react";
import { Bell, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^http/, "ws");

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);

  const load = async () => {
    if (!user) return;
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  };

  useEffect(() => {
    if (user && user.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user || !user.id) return;
    let stopped = false;
    let ws;
    const connect = async () => {
      try {
        const { data } = await api.get("/auth/ws-token");
        if (stopped) return;
        ws = new WebSocket(`${WS_URL}/api/ws/notify?token=${encodeURIComponent(data.token)}`);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "notification") setItems(prev => [msg.notification, ...prev]);
          } catch {}
        };
      } catch {}
    };
    connect();
    return () => { stopped = true; if (ws) ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const unread = items.filter(i => !i.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  if (!user || !user.id) return null;

  return (
    <div className="relative">
      <button onClick={()=>setOpen(!open)} className="p-2 relative" data-testid="notification-bell">
        <Bell className="w-5 h-5"/>
        {unread > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 text-[10px] font-bold flex items-center justify-center text-white"
                style={{background:"var(--secondary)", borderRadius:"50%"}} data-testid="notification-count">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[var(--surface)] border border-[var(--border)] shadow-xl z-50" data-testid="notification-panel">
          <div className="p-3 flex justify-between items-center border-b border-[var(--border)]">
            <span className="font-semibold text-sm">Notifications</span>
            <div className="flex gap-2 items-center">
              {unread > 0 && <button onClick={markAll} className="text-xs underline" style={{color:"var(--primary-text)"}} data-testid="mark-all-read">Mark all read</button>}
              <button onClick={()=>setOpen(false)}><X className="w-4 h-4"/></button>
            </div>
          </div>
          <div className="max-h-96 overflow-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm" style={{color:"var(--text-muted)"}}>No notifications</div>
            ) : items.map(n => (
              <Link key={n.id} to={n.link || "#"} onClick={()=>setOpen(false)}
                    className={`block p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--surface-2)] ${!n.read?"bg-[var(--surface-2)]/50":""}`}
                    data-testid={`notification-${n.id}`}>
                <div className="text-sm" style={{color:"var(--text)"}}>{n.body}</div>
                <div className="text-[11px] mt-1" style={{color:"var(--text-muted)"}}>{timeAgo(n.created_at)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
